import * as React from 'react';

/* ------------------------------------------------------------------ *
 * Minimal Web Speech API type surface (browser-native, no @types dep)
 * ------------------------------------------------------------------ */

interface SpeechRecognitionAlternative {
  readonly transcript: string;
  readonly confidence: number;
}

interface SpeechRecognitionResult {
  readonly isFinal: boolean;
  readonly length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
}

interface SpeechRecognitionResultList {
  readonly length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface SpeechRecognitionLike extends EventTarget {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: ((event: Event) => void) | null;
  onstart: ((event: Event) => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

function getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  const w = window as unknown as {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return w.SpeechRecognition ?? w.webkitSpeechRecognition ?? null;
}

/* ------------------------------------------------------------------ *
 * Hook
 * ------------------------------------------------------------------ */

export type SpeechRecognitionStatus =
  | 'unsupported'
  | 'idle'
  | 'listening'
  | 'paused'
  | 'error';

export interface UseSpeechRecognitionResult {
  /** `true` only on browsers with the Web Speech API */
  supported: boolean;
  /** Current high-level state */
  status: SpeechRecognitionStatus;
  /** Human-readable error message (empty when no error) */
  errorMessage: string;
  /** Latest interim (non-final) transcript fragment — cleared on each utterance */
  interimTranscript: string;
  /** Final recognised utterances since last read (accumulates until consumed) */
  finalTranscript: string;
  /** Turn listening on/off */
  setEnabled: (enabled: boolean) => void;
  /** Whether the mic is currently active */
  enabled: boolean;
  /** Clear accumulated final transcript after the consumer has processed it */
  consumeFinalTranscript: () => void;
}

const RESTART_DELAY_MS = 250;

export function useSpeechRecognition(
  options?: { lang?: string }
): UseSpeechRecognitionResult {
  const lang = options?.lang ?? 'en-AU';
  const ctorRef = React.useRef<SpeechRecognitionCtor | null>(getSpeechRecognitionCtor());
  const supported = ctorRef.current !== null;

  const [status, setStatus] = React.useState<SpeechRecognitionStatus>(
    supported ? 'idle' : 'unsupported'
  );
  const [errorMessage, setErrorMessage] = React.useState('');
  const [interimTranscript, setInterimTranscript] = React.useState('');
  const [finalTranscript, setFinalTranscript] = React.useState('');
  const [enabled, setEnabledState] = React.useState(false);

  // Refs that the recognition callbacks need to read without re-creating the
  // recognition instance on every render.
  const recognitionRef = React.useRef<SpeechRecognitionLike | null>(null);
  const enabledRef = React.useRef(false);
  const visibleRef = React.useRef(true);
  const restartTimerRef = React.useRef<number | null>(null);
  const manualStopRef = React.useRef(false);

  enabledRef.current = enabled;

  const cleanupRecognition = React.useCallback(() => {
    if (restartTimerRef.current !== null) {
      window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = null;
    }
    const rec = recognitionRef.current;
    if (rec) {
      manualStopRef.current = true; // suppress auto-restart
      try {
        rec.abort();
      } catch {
        // already stopped
      }
      recognitionRef.current = null;
    }
  }, []);

  const startRecognition = React.useCallback(() => {
    const Ctor = ctorRef.current;
    if (!Ctor) return;
    // Don't start if user disabled, page hidden, or instance already running
    if (!enabledRef.current) return;
    if (!visibleRef.current) return;
    if (recognitionRef.current) return;

    manualStopRef.current = false;
    const rec = new Ctor();
    rec.lang = lang;
    rec.continuous = true;
    rec.interimResults = true;
    rec.maxAlternatives = 1;

    rec.onstart = () => {
      setStatus('listening');
      setErrorMessage('');
    };

    rec.onresult = (event: SpeechRecognitionEvent) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        if (!result) continue;
        const alt = result[0];
        if (!alt) continue;
        if (result.isFinal) {
          final += alt.transcript;
        } else {
          interim += alt.transcript;
        }
      }
      if (interim) setInterimTranscript(interim);
      else if (final) setInterimTranscript('');
      if (final) {
        setFinalTranscript((prev) => (prev ? prev + ' ' : '') + final.trim());
      }
    };

    rec.onerror = (event: SpeechRecognitionErrorEvent) => {
      const err = event.error;
      // "no-speech" and "aborted" are transient — auto-restart handles them.
      if (err === 'no-speech' || err === 'aborted') return;
      if (err === 'not-allowed' || err === 'service-not-allowed') {
        setStatus('error');
        setErrorMessage('Microphone permission denied. Allow mic access to use voice.');
        setEnabledState(false);
        enabledRef.current = false;
        return;
      }
      if (err === 'audio-capture') {
        setStatus('error');
        setErrorMessage('No microphone was found on this device.');
        setEnabledState(false);
        enabledRef.current = false;
        return;
      }
      if (err === 'network') {
        setStatus('error');
        setErrorMessage('Voice recognition network error. Will retry.');
        return;
      }
      // Generic — don't disable, let auto-restart try.
      setStatus('error');
      setErrorMessage(`Voice error: ${err}`);
    };

    rec.onend = () => {
      recognitionRef.current = null;
      setInterimTranscript('');
      // Auto-restart unless the user explicitly stopped or page is hidden
      if (manualStopRef.current) {
        setStatus(enabledRef.current ? 'paused' : 'idle');
        return;
      }
      if (!enabledRef.current) {
        setStatus('idle');
        return;
      }
      if (!visibleRef.current) {
        setStatus('paused');
        return;
      }
      // Schedule restart (recognition sessions time out after ~60s of silence)
      if (restartTimerRef.current !== null) window.clearTimeout(restartTimerRef.current);
      restartTimerRef.current = window.setTimeout(() => {
        restartTimerRef.current = null;
        if (enabledRef.current && visibleRef.current) {
          startRecognition();
        }
      }, RESTART_DELAY_MS);
    };

    recognitionRef.current = rec;
    try {
      rec.start();
    } catch {
      // start() throws if already started — swallow and let onend retry
      recognitionRef.current = null;
    }
  }, [lang]);

  // React to enabled changes
  React.useEffect(() => {
    if (!supported) return;
    if (enabled) {
      startRecognition();
    } else {
      cleanupRecognition();
      setStatus('idle');
      setInterimTranscript('');
    }
    return () => {
      if (!enabled) cleanupRecognition();
    };
  }, [enabled, supported, startRecognition, cleanupRecognition]);

  // Pause when page is hidden (background tab / screen off)
  React.useEffect(() => {
    if (!supported) return;
    const onVisibility = () => {
      const visible = document.visibilityState === 'visible';
      visibleRef.current = visible;
      if (!visible) {
        // Stop current session; onend will set paused
        const rec = recognitionRef.current;
        if (rec) {
          manualStopRef.current = true;
          try {
            rec.stop();
          } catch {
            // ignore
          }
        }
      } else if (enabledRef.current) {
        // Page visible again — restart if enabled
        if (!recognitionRef.current) {
          manualStopRef.current = false;
          startRecognition();
        }
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [supported, startRecognition]);

  // Full cleanup on unmount
  React.useEffect(() => {
    return () => {
      cleanupRecognition();
    };
  }, [cleanupRecognition]);

  const setEnabled = React.useCallback((value: boolean) => {
    setEnabledState(value);
  }, []);

  const consumeFinalTranscript = React.useCallback(() => {
    setFinalTranscript('');
  }, []);

  return {
    supported,
    status,
    errorMessage,
    interimTranscript,
    finalTranscript,
    setEnabled,
    enabled,
    consumeFinalTranscript
  };
}
